import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Check, HeartPulse, Truck, Users, Shield, Wrench, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const Services = () => {
  const services = [
    {
      icon: <HeartPulse className="h-12 w-12 text-[#7bb5d4]" />,
      title: 'Insurance Coordination',
      description: 'We work with Medicare, Medicaid, and most major insurance providers to ensure you get the coverage you deserve. Our team handles all the paperwork and verification.'
    },
    {
      icon: <Truck className="h-12 w-12 text-[#7bb5d4]" />,
      title: 'Delivery & Setup',
      description: 'Professional delivery and setup services available for all major equipment. Our technicians ensure everything is properly installed and functioning.'
    },
    {
      icon: <Users className="h-12 w-12 text-[#7bb5d4]" />,
      title: 'Expert Consultation',
      description: 'Our experienced staff provides personalized recommendations based on your specific needs and lifestyle. We take time to understand your requirements.'
    },
    {
      icon: <Shield className="h-12 w-12 text-[#7bb5d4]" />,
      title: 'Quality Assurance',
      description: 'Every product undergoes rigorous quality checks. We only partner with trusted manufacturers known for durability and reliability.'
    },
    {
      icon: <Wrench className="h-12 w-12 text-[#7bb5d4]" />,
      title: 'Maintenance & Repair',
      description: 'Comprehensive maintenance plans and repair services to keep your equipment functioning at peak performance for years to come.'
    },
    {
      icon: <Phone className="h-12 w-12 text-[#7bb5d4]" />,
      title: '24/7 Support',
      description: 'Our customer support team is available around the clock to answer questions, provide guidance, and assist with any issues.'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Consultation',
      description: 'Contact us for a free consultation. We\'ll discuss your needs and recommend appropriate solutions.'
    },
    {
      step: '2',
      title: 'Insurance Verification',
      description: 'We verify your insurance coverage and handle all the necessary paperwork on your behalf.'
    },
    {
      step: '3',
      title: 'Product Selection',
      description: 'Choose from our extensive catalog of quality medical equipment with expert guidance.'
    },
    {
      step: '4',
      title: 'Delivery & Setup',
      description: 'We deliver and professionally set up your equipment at your convenience.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Our Services | Kalorama Medical"
        description="Comprehensive medical equipment services including insurance coordination, delivery & setup, expert consultation, maintenance & repair, and 24/7 support."
        keywords="medical equipment services, insurance coordination, equipment delivery, equipment setup, maintenance, repair"
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our Services
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Comprehensive support for all your medical equipment needs
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a5f]">
              What We Offer
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From initial consultation to ongoing support, we're with you every step of the way
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-[#1e3a5f]">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a5f]">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our simple four-step process makes getting the equipment you need easy and stress-free
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a5f] to-[#7bb5d4] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#1e3a5f]">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#7bb5d4] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-[#1e3a5f]">
              Why Choose Kalorama Medical?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Over 19 years of industry experience',
                'Licensed and certified professionals',
                'Extensive product selection',
                'Competitive pricing',
                'Insurance accepted and filed',
                'Local delivery and setup available',
                'Ongoing support and maintenance',
                'Satisfaction guaranteed'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                  <Check className="h-6 w-6 text-[#7bb5d4] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Contact us today for a free consultation and let us help you find the perfect equipment
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-blue-50 text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              Schedule Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
